/**
 * The phase vocabulary of MU_POWER electrical networks.
 *
 * A component carries either a single phase (A, B or C), a phase pair (AB, AC or BC) or all
 * three phases (ABC). The solver only accepts pairs written in alphabetical order.
 */

export const SINGLE_PHASES = ["A", "B", "C"] as const;
export type SinglePhase = (typeof SINGLE_PHASES)[number];

export const PHASE_PAIRS = ["AB", "AC", "BC"] as const;
export type PhasePair = (typeof PHASE_PAIRS)[number];

/** A phase an override point can take. Full three-phase (ABC) is never an override target. */
export type OverridePhase = SinglePhase | PhasePair;

export const ALL_PHASES = "ABC" as const;

/** The phases present on a run of wires: a single phase, a pair, or all three. */
export type PhaseSet = OverridePhase | typeof ALL_PHASES;

export const isSinglePhase = (value: unknown): value is SinglePhase => SINGLE_PHASES.includes(value as SinglePhase);

export const isPhasePair = (value: unknown): value is PhasePair => PHASE_PAIRS.includes(value as PhasePair);

export const isOverridePhase = (value: unknown): value is OverridePhase => isSinglePhase(value) || isPhasePair(value);

/** The letters of a phase set, in alphabetical order. */
export const phaseLetters = (phases: PhaseSet): SinglePhase[] => [...phases] as SinglePhase[];

/** The phases of the given width contained in a phase set, in alphabetical order. */
export const subsetsOfSize = (phases: PhaseSet, width: 1 | 2): OverridePhase[] => {
  const letters = phaseLetters(phases);
  if (width === 1) return letters;
  return letters.flatMap((first, index) => letters.slice(index + 1).map((second) => `${first}${second}` as PhasePair));
};

/**
 * A phase is compatible with a two-phase pair if the phase is a "subset" of the pair.
 *
 * For example, the phase "A" is compatible with pairs "AB" or "AC", but not "BC". The phase "ABC" is treated
 * as being compatible with all pairs, even though I have no idea what that would mean in practice.
 */
export const isPhaseCompatibleWithPair = (phase: string, pair: string): boolean =>
  phase === "ABC" || [...phase].every((letter) => pair.includes(letter));
